"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDuplicatesKey = exports.PageStateDeduper = exports.mergeDedupedPageStates = void 0;
exports.dedupePageStates = dedupePageStates;
const mobilePlatform_1 = require("../mobilePlatform");
const createElementFingerprint_1 = require("./createElementFingerprint");
const createFingerprints_1 = require("./createFingerprints");
const uniqueNodeKeySet_1 = require("./uniqueNodeKeySet");
/**
 * Merges multiple DedupedPageStateResults (from dedupePageStates) into one
 * DedupedResult per rule.
 *
 * @example
 * For input like this:
 *
 *   page_state_1 violations:
 *     rule_a: [node_1]
 *     rule_b: [node_2]
 *   page_state_2 violations:
 *     rule_a: [node_1]
 *     rule_c: [node_3]
 *   page_state_3 violations:
 *     rule_a: [node_1, node_4]
 *
 * The outcome after dedupePageStates -> mergeDedupedPageStates will be:
 *
 *   rule_a:
 *     uniques:
 *       page_state_1@node_1
 *       page_state_3@node_4
 *     duplicates:
 *       node_1: 2
 *   rule_b:
 *     uniques:
 *       page_state_1@node_2
 *     duplicates: {}
 *   rule_c:
 *     uniques:
 *       page_state_2@node_3
 *     duplicates: {}
 */
const mergeDedupedPageStates = (resultsByPageState) => {
    const resultsByRule = new Map();
    for (const pageStateResult of resultsByPageState) {
        for (const pageStateRuleResult of pageStateResult.result) {
            const ruleId = pageStateRuleResult.id;
            const sessionRuleResult = resultsByRule.get(ruleId);
            if (!sessionRuleResult) {
                // No previous page state had results for this rule; seed the session-wide
                // rule result from this page state's rule result
                resultsByRule.set(ruleId, structuredClone(pageStateRuleResult));
            }
            else {
                // Previous page state(s) had results for this rule. We assume that the input
                // was already deduplicated against previous page states, so we can just append
                // uniques and dupes from this page state to the session-wide rule result without
                // doing any additional tests of uniqueness.
                sessionRuleResult.uniques.push(...pageStateRuleResult.uniques);
                for (const [key, count] of Object.entries(pageStateRuleResult.duplicates)) {
                    sessionRuleResult.duplicates[key] =
                        (sessionRuleResult.duplicates[key] || 0) + count;
                }
            }
        }
    }
    return resultsByRule;
};
exports.mergeDedupedPageStates = mergeDedupedPageStates;
/** `dedupePageStates` one page state at a time, for callers that drive their own loop. */
class PageStateDeduper {
    uniqueNodeKeys = new uniqueNodeKeySet_1.UniqueNodeKeySet();
    #options;
    constructor(options = {}) {
        this.#options = options;
    }
    /** `null` for a skipped URL, which produces no page state. */
    dedupe(result) {
        if (result.skipped_url) {
            return null;
        }
        const dedupedPageState = prepareWatcherResultForDeduplication(result, this.#options);
        for (const ruleResult of dedupedPageState.result) {
            reclassifySeenUniquesAsDuplicates(ruleResult, this.uniqueNodeKeys);
        }
        return dedupedPageState;
    }
}
exports.PageStateDeduper = PageStateDeduper;
/**
 * Deduplicates multiple page-states'-worth of Watcher results into a
 * separate result set per page-state.
 *
 * This is similar to what you'd get by calling `dedupeAllPageStates` on each
 * page-state individually, except that a NodeResult in a later page-state will
 * be considered to be a "duplicate" rather than a "unique" if the same rule-node
 * pair already appeared as a unique in an earlier page-state.
 *
 * @example
 * For input like this:
 *
 *   page_state_1 violations:
 *     rule_a: [node_1]
 *     rule_b: [node_2]
 *   page_state_2 violations:
 *     rule_a: [node_1]
 *     rule_c: [node_3]
 *   page_state_3 violations:
 *     rule_a: [node_1, node_4]
 *
 * The outcome after deduplication will be:
 *
 *   page_state_1:
 *     rule_a:
 *       uniques: [node_1]
 *     rule_b:
 *       uniques: [node_2]
 *   page_state_2:
 *     rule_a:
 *       uniques: []
 *       duplicates: {node_1: 1}
 *     rule_c:
 *       uniques: [node_3]
 *   page_state_3:
 *     rule_a:
 *       uniques: [node_4]
 *       duplicates: {node_1: 1}
 */
async function* dedupePageStates(results, options = {}) {
    const deduper = new PageStateDeduper(options);
    for await (const originalResult of results) {
        const dedupedPageState = deduper.dedupe(originalResult);
        if (dedupedPageState) {
            yield dedupedPageState;
        }
    }
}
/**
 * Updates *both* result *and* uniqueNodeKeys in-place, moving any
 * previously-seen uniques in result.uniques to result.duplicates
 * and adding any new uniques to uniqueNodeKeys.
 * @param result The result to update
 * @param uniqueNodeKeys The set of previously-seen unique nodes
 */
const reclassifySeenUniquesAsDuplicates = (result, uniqueNodeKeys) => {
    const newUniques = [];
    for (const candidate of result.uniques) {
        // A target-less node passes through as a unique by design, not for want
        // of a check: there is nothing to dedup it on.
        if (!candidate.target || !candidate.target.length) {
            newUniques.push(candidate);
            continue;
        }
        const duplicateFingerprint = uniqueNodeKeys.getDuplicateFingerprint(candidate);
        if (duplicateFingerprint) {
            // This node is a duplicate of one from a previous page state;
            // omit it from newUniques, include it in duplicates
            result.duplicates[duplicateFingerprint] =
                (result.duplicates[duplicateFingerprint] || 0) + 1;
        }
        else {
            // This node is a new unique (even among previous page states);
            // include it in newUniques, omit it from duplicates
            newUniques.push(candidate);
            // Only uniques are added to the unique node key set. Otherwise, duplicate
            // non-primary fingerprints would override the primary fingerprint of previously
            // seen non-primary fingerprints and mess up duplicate counts.
            uniqueNodeKeys.upsert(candidate);
        }
    }
    result.uniques = newUniques;
};
/**
 *
 * @param node - The node to get build the duplicates key for
 * @returns  The key to use for the duplicates object
 */
const getDuplicatesKey = (node) => node.fingerprints
    ? node.fingerprints[0]
    : `${node.url}_${node.stringifiedTarget}`; // Fallback for support in walnut - will be removed
exports.getDuplicatesKey = getDuplicatesKey;
/** Converts a WatcherResult to the shape of a DedupedPageStateResult, so it can be deduped later */
function prepareWatcherResultForDeduplication(result, options) {
    const resultInfo = {
        page_state: result.page_state,
        url: result.url,
        created_at: result.created_at,
        screenshot_id: result.screenshot_id,
        error: result.error,
        skipped_url: result.skipped_url,
        created_by_user_id: result.created_by_user_id ?? null,
        // this will push the top-level metadata to the page state and ultimately
        // the DedupedNode, which is what Launchpad expects to render issues
        metadata: result.metadata ?? null,
        userTags: result.userTags ?? null,
        test_context: result.test_context ?? null
    };
    const isMobile = (0, mobilePlatform_1.isMobileMetadata)(result.metadata);
    const dedupedResults = result.violations.map(v => prepareViolationForDeduplication({
        resultInfo,
        violation: v,
        pageStateId: result.id,
        options,
        isMobile
    }));
    return {
        ...resultInfo,
        id: result.id,
        result: dedupedResults
    };
}
/** Converts a WatcherViolation to the shape of a DedupedResult, so it can be deduped later */
function prepareViolationForDeduplication({ resultInfo, violation: { id, nodes, impact, tags, description, help, helpUrl }, pageStateId, options: { generateElementFingerprints }, isMobile }) {
    // We skip running any actual deduplication logic here because we assume a
    // few prerequisites that are guaranteed by axe-core:
    //
    // - no single RuleResult references multiple copies of the same node
    // - every distinct DOM node has a unique target
    // - every distinct DOM node has a unique xpath
    //
    // These guarantee that all nodes within the original WatcherViolation are
    // already unique compared to each other.
    const uniques = nodes.map(node => ({
        html: node.html,
        target: node.target,
        xpath: node.xpath,
        ancestry: node.ancestry,
        stringifiedTarget: node.target && node.target.length
            ? (0, createFingerprints_1.createStringifiedTarget)({
                ruleId: id,
                node,
                url: resultInfo.url
            })
            : undefined,
        fingerprints: node.target && node.target.length
            ? (0, createFingerprints_1.createFingerprints)({
                ruleId: id,
                node,
                url: resultInfo.url
            })
            : undefined,
        ...(generateElementFingerprints &&
            node.target &&
            node.target.length && {
            elementFingerprint: isMobile
                ? (0, createElementFingerprint_1.createMobileElementFingerprint)(node.target)
                : (0, createElementFingerprint_1.createElementFingerprint)({
                    target: node.target,
                    html: node.html,
                    xpath: node.xpath,
                    ancestry: node.ancestry
                })
        }),
        // node is always a violation (not a pass), so it will always have a failureSummary
        failureSummary: node.failureSummary,
        isNewIssue: node.isNewIssue,
        advanced: node.advanced,
        mobileLabelInput: node.mobileLabelInput,
        originatingPageStateId: pageStateId,
        ...resultInfo
    }));
    return {
        id,
        pageStateId,
        impact: impact,
        tags,
        description,
        help,
        helpUrl,
        uniques,
        duplicates: {}
    };
}
//# sourceMappingURL=watcherDedupe.js.map